import { useState, useEffect, useRef } from 'react';
import { MapContainer, Polygon, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createIsochrone } from '../../../../utils/distanceUtils';
import { api, apiRoutes } from '../../../../api';
import BaseLayers from './BaseLayers';
import { updateAnchor } from '../../services/propertyRequestService';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const PALETTE = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00acc1'];
const MODE_TO_OSRM = { drive: 'driving', walk: 'walking', bike: 'cycling', transit: 'driving' };
const MODE_LABELS  = { walk: 'پیاده', bike: 'دوچرخه', drive: 'خودرو', transit: 'حمل‌ونقل عمومی' };

function pip(lngLat, polygon) {
    const [px, py] = lngLat;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
        if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
}

function getAnchorLatLon(anchor) {
    const loc = anchor.location;
    if (!loc) return null;
    // API returns GeoJSON Point: { type: "Point", coordinates: [lng, lat] }
    if (loc.type === 'Point' && Array.isArray(loc.coordinates)) {
        const [lng, lat] = loc.coordinates;
        if (isFinite(lat) && isFinite(lng)) return { lat, lon: lng };
    }
    // Plain object: { lat, lon } or { lat, lng }
    const lat = loc.lat ?? loc.latitude;
    const lon = loc.lon ?? loc.lng ?? loc.longitude;
    if (isFinite(lat) && isFinite(lon)) return { lat, lon };
    return null;
}

function FitBounds({ anchors }) {
    const map = useMap();
    useEffect(() => {
        if (!anchors.length) return;
        const pts = anchors
            .map(a => { const ll = getAnchorLatLon(a); return ll ? [ll.lat, ll.lon] : null; })
            .filter(Boolean);
        if (!pts.length) return;
        // Use animate:false — the map may be in a hidden tab (display:none) with
        // zero dimensions, which makes Leaflet's animation loop produce NaN.
        if (pts.length === 1) {
            map.setView(pts[0], 13, { animate: false });
        } else {
            map.fitBounds(pts, { padding: [60, 60], maxZoom: 14, animate: false });
        }
    }, [anchors, map]);
    return null;
}

async function detectCityId(lat, lon) {
    try { const r = await api.post(apiRoutes.locations.findNeighborhoodByPoint, { lat, lon }); return r.data?.neighborhood?.city ?? null; }
    catch { return null; }
}

export default function CommuteIsochroneMap({ anchors, onNeighborhoodsChange, onPolygonsChange, requestId }) {
    const [isoData, setIsoData] = useState({});
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [matchedNbrs, setMatchedNbrs] = useState([]);
    const [nbrLoading, setNbrLoading] = useState(false);
    const computedRef = useRef(new Set());

    useEffect(() => {
        anchors.forEach((anchor, idx) => {
            const key = anchor.id ?? `tmp-${idx}`;
            if (computedRef.current.has(key)) return;
            const ll = getAnchorLatLon(anchor);
            if (!ll) return; // skip anchors with no valid location
            computedRef.current.add(key);
            const color = PALETTE[idx % PALETTE.length];
            setIsoData(prev => ({ ...prev, [key]: { color, loading: true, coords: null, error: null } }));
            createIsochrone({ lat: ll.lat, lng: ll.lon }, anchor.max_duration_min, MODE_TO_OSRM[anchor.mode] || 'driving', 24)
                .then(coords => setIsoData(prev => ({ ...prev, [key]: { color, loading: false, coords, error: null } })))
                .catch(e => setIsoData(prev => ({ ...prev, [key]: { color, loading: false, coords: null, error: String(e) } })));
        });
    }, [anchors]);

    const nbrLoadedRef = useRef(false);

    useEffect(() => {
        const keys = anchors.map((a, idx) => a.id ?? `tmp-${idx}`);
        if (!keys.length) return;
        const allDone = keys.every(k => isoData[k] && !isoData[k].loading);
        const anyCoords = keys.some(k => isoData[k]?.coords);
        if (!allDone || !anyCoords || nbrLoadedRef.current) return;
        nbrLoadedRef.current = true;
        setNbrLoading(true);
        const ll0 = getAnchorLatLon(anchors[0]);
        if (!ll0) { setNbrLoading(false); return; }
        detectCityId(ll0.lat, ll0.lon)
            .then(cityId => cityId ? api.get(`/neighborhoods/?city=${cityId}&page_size=500`).then(r => r.data?.results ?? r.data) : [])
            .then(data => setNeighborhoods(data || []))
            .catch(() => setNeighborhoods([]))
            .finally(() => setNbrLoading(false));
    }, [isoData, anchors]); // eslint-disable-line

    useEffect(() => {
        if (!neighborhoods.length) { setMatchedNbrs([]); return; }
        const keys = anchors.map((a, idx) => a.id ?? `tmp-${idx}`);
        const allPolys = keys.map(k => isoData[k]?.coords).filter(Boolean);
        if (!allPolys.length) { setMatchedNbrs([]); return; }
        // Wait until every anchor's isochrone is ready
        const allDone = keys.every(k => isoData[k] && !isoData[k].loading);
        if (!allDone) return;

        // For the display chip list: neighborhoods inside ALL polygons (intersection)
        const intersection = neighborhoods.filter(
            n => n.center && allPolys.every(poly => pip([n.center.lon, n.center.lat], poly))
        );
        setMatchedNbrs(intersection.map(n => ({ id: n.id, name: n.name })));

        // Per-anchor: save only the neighborhoods inside THAT anchor's own isochrone
        if (requestId) {
            anchors.forEach((a, idx) => {
                if (!a.id) return;
                const key = a.id ?? `tmp-${idx}`;
                const poly = isoData[key]?.coords;
                if (!poly) return;
                const ownIds = neighborhoods
                    .filter(n => n.center && pip([n.center.lon, n.center.lat], poly))
                    .map(n => n.id);
                updateAnchor(requestId, a.id, { neighborhood_names: ownIds }).catch(() => {});
            });
        }
    }, [neighborhoods, isoData, anchors]); // eslint-disable-line

    useEffect(() => { if (onNeighborhoodsChange) onNeighborhoodsChange(matchedNbrs.map(n => n.id)); }, [matchedNbrs]); // eslint-disable-line
    useEffect(() => {
        if (!onPolygonsChange) return;
        onPolygonsChange(Object.values(isoData).filter(d => d.coords).map(d => d.coords));
    }, [isoData]); // eslint-disable-line

    const anyLoading = Object.values(isoData).some(d => d.loading);
    const allReady = anchors.length > 0 && !anyLoading;

    return (
        <div className="cim-wrap">
            {anchors.length > 0 && (
                <div className="cim-legend">
                    {anchors.map((a, idx) => {
                        const key = a.id ?? `tmp-${idx}`;
                        const entry = isoData[key];
                        return (
                            <div key={key} className="cim-legend-item">
                                <span className="cim-legend-dot" style={{ background: PALETTE[idx % PALETTE.length] }} />
                                <span className="cim-legend-label">{a.label}<span className="cim-legend-sub"> — {MODE_LABELS[a.mode]} · {a.max_duration_min} دقیقه</span></span>
                                {entry?.loading && <span className="cim-computing">در حال محاسبه...</span>}
                                {entry?.error && <span className="cim-error-badge">خطا</span>}
                            </div>
                        );
                    })}
                </div>
            )}
            {anyLoading && <p className="cim-status">⏳ در حال محاسبه محدوده دسترسی...</p>}
            {anchors.length > 1 && allReady && <p className="cim-intersect-hint">✦ ناحیه تلاقی همه رنگ‌ها بهترین منطقه برای سکونت است.</p>}
            <div className="cim-map-container">
                <MapContainer center={[35.6892, 51.389]} zoom={11} className="cim-map">
                    <BaseLayers position="topright" />
                    <FitBounds anchors={anchors} />
                    {anchors.map((a, idx) => {
                        const key = a.id ?? `tmp-${idx}`; const entry = isoData[key];
                        if (!entry?.coords) return null;
                        return (
                            <Polygon key={key} positions={entry.coords.map(([lng, lat]) => [lat, lng])}
                                pathOptions={{ color: entry.color, fillColor: entry.color, fillOpacity: 0.18, weight: 2, opacity: 0.75 }}>
                                <Tooltip sticky>{a.label} — {MODE_LABELS[a.mode]} · {a.max_duration_min} دقیقه</Tooltip>
                            </Polygon>
                        );
                    })}
                    {anchors.map((a, idx) => {
                        const key = a.id ?? `tmp-${idx}`; const color = PALETTE[idx % PALETTE.length];
                        const ll = getAnchorLatLon(a);
                        if (!ll) return null;
                        return (
                            <Marker key={`m-${key}`} position={[ll.lat, ll.lon]}
                                icon={L.divIcon({ className: '', html: `<div style="background:${color};border:2px solid #fff;width:14px;height:14px;border-radius:50%"></div>`, iconSize: [14, 14], iconAnchor: [7, 7] })}>
                                <Tooltip permanent direction="top" offset={[0, -10]}>{a.label}</Tooltip>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>
            {allReady && (
                <div className="cim-nbr-section">
                    {nbrLoading ? <p className="cim-status">🔍 در حال جستجوی محله‌ها...</p>
                        : matchedNbrs.length > 0 ? (
                            <>
                                <p className="cim-nbr-title">محله‌های مناسب ({matchedNbrs.length} محله){anchors.length > 1 && ' — در محدوده دسترسی همه مقاصد'}</p>
                                <div className="cim-nbr-chips">{matchedNbrs.map(n => <span key={n.id} className="cim-nbr-chip">{n.name}</span>)}</div>
                            </>
                        ) : <p className="cim-nbr-empty">{anchors.length > 1 ? 'محله‌ای یافت نشد. محدوده زمانی را افزایش دهید.' : 'محله‌ای در این محدوده یافت نشد.'}</p>
                    }
                </div>
            )}
            {anchors.length === 0 && <p className="cim-empty">پس از افزودن مقاصد، محدوده دسترسی نمایش داده می‌شود.</p>}
        </div>
    );
}
